import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, User, LogOut, ChevronDown, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/", label: "Accueil" },
    { href: "/particuliers", label: "Particuliers" },
    { href: "/professionnels", label: "Professionnels" },
    { href: "/contact", label: "Contact" },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled 
          ? "bg-white/95 backdrop-blur-md shadow-lg shadow-gray-100/50" 
          : "bg-white"
      }`} 
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="logo-link">
            <img 
              src="/logo-iks.png" 
              alt="IKS Propreté & Services" 
              className="h-20 lg:h-24 w-auto object-contain"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`relative px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                  isActive(link.href) 
                    ? "text-[#003264] bg-[#003264]/5" 
                    : "text-gray-600 hover:text-[#003264] hover:bg-gray-50"
                }`}
              >
                {link.label}
                {isActive(link.href) && (
                  <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 bg-[#003264] rounded-full"></span>
                )}
              </Link>
            ))}
          </nav>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-3">
            <a 
              href="tel:0625168027" 
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-[#003264] hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span className="font-medium">06 25 16 80 27</span>
            </a>
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="border-[#003264] text-[#003264] hover:bg-[#003264] hover:text-white">
                    <User className="w-4 h-4 mr-2" />
                    {user.prenom}
                    <ChevronDown className="w-4 h-4 ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium text-gray-900">{user.prenom} {user.nom}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} className="cursor-pointer text-[#003264]">
                      <Shield className="w-4 h-4 mr-2" />
                      Administration
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate("/mon-espace")} className="cursor-pointer">
                    Mon espace
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-xs text-gray-500">
                    Code parrainage: <span className="font-mono ml-1">{user.code_parrainage}</span>
                  </DropdownMenuItem>
                  {user.credits > 0 && (
                    <DropdownMenuItem className="text-xs text-emerald-600 font-medium">
                      Crédit: {user.credits}€
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/connexion">
                <Button className="bg-[#003264] hover:bg-[#002244] text-white px-6 btn-shine">
                  Connexion
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6 text-gray-700" /> : <Menu className="w-6 h-6 text-gray-700" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div 
        className={`lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-xl transition-all duration-300 ${
          mobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible"
        }`}
        data-testid="mobile-menu"
      >
        <nav className="px-6 py-4 space-y-1">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`flex items-center px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                isActive(link.href) 
                  ? "bg-[#003264]/5 text-[#003264]" 
                  : "text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          
          {/* Phone */}
          <a 
            href="tel:0625168027" 
            className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-xl transition-colors"
          >
            <Phone className="w-4 h-4" />
            06 25 16 80 27
          </a>
          
          <div className="pt-4 border-t border-gray-100 mt-4 space-y-2">
            {user ? (
              <>
                <div className="px-4 py-2">
                  <p className="text-sm font-medium text-gray-900">{user.prenom} {user.nom}</p>
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
                
                {/* Mon espace client */}
                <Link
                  to="/mon-espace"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-[#003264] bg-[#003264]/5 rounded-xl hover:bg-[#003264]/10 transition-colors"
                  data-testid="mobile-mon-espace"
                >
                  <User className="w-4 h-4" />
                  Mon espace client
                </Link>
                
                {/* Administration (si admin) */}
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-[#003264] bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors"
                    data-testid="mobile-admin-link"
                  >
                    <Shield className="w-4 h-4" />
                    Administration
                  </Link>
                )}
                
                <button 
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 text-sm font-medium hover:bg-red-50 rounded-xl transition-colors"
                  data-testid="mobile-logout"
                >
                  <LogOut className="w-4 h-4" />
                  Déconnexion
                </button>
              </>
            ) : (
              <Link to="/connexion" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-[#003264] hover:bg-[#002244] text-white py-6" data-testid="mobile-connexion-btn">
                  Connexion
                </Button>
              </Link>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
};
